'use client';

import { toPascalCase } from '@/utils/string-utils';
import { IBaseComponentProps } from '@/lib/types';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { IHeader } from '@/.generated';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { DesktopHeader } from './Desktop Header/DesktopHeader';
import { MobileHeader } from './Mobile Header/MobileHeader';
import { desktopHeaderVariants } from './Header.styles';
import { useState } from 'react';
import { getCSLPAttributes } from '@/utils/type-guards';
import { SearchBar } from '@/helpers/Search/SearchBar/SearchBar';
import { LanguageSelector } from '../LanguageSelector/LanguageSelector';
import { MobileNav } from './MobileNav';
import { localizeHref } from '@/utils/localize-href';
import { trackAdobeClick } from '@/utils/adobe/click';
import {
  useRedirectBaseUrl,
  applyRedirectBaseUrl,
} from '@/web-component/redirect-base-url-context';
import { useGlobalLabels } from '@/context/GlobalLabelContext';

export type HeaderProps = IBaseComponentProps &
  IHeader & {
    pageUid?: string;
    pageContentTypeUid?: string;
    hasAlternateLanguage?: boolean;
    englishUrl?: string;
    spanishUrl?: string;
    redirectSearchBaseUrl?: string;
    // Pre-rendered emergency alert node passed from the server parent (async CMS fetch happens there)
    emergencyAlert?: React.ReactNode;
  };

export const Logo = (props: HeaderProps) => {
  const { desktop_logo, mobile_logo, logo_link } = props.logo_section || {};
  const isLogoLink = logo_link?.href;
  const params = useParams();
  const currentLocale = (params.locale as string) || undefined;
  // Web-component-only: prefix the logo href with the external base when provided
  const redirectBaseUrl = useRedirectBaseUrl();

  // Fire the Adobe Click event for the header logo
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackAdobeClick(
      {
        clickName: logo_link?.title || 'Logo',
        clickLocation: 'Header',
        clickType: 'header-logo-link',
      },
      e
    );
  };

  if (isLogoLink) {
    const localized = localizeHref(logo_link?.href, currentLocale) || '/';
    const href = applyRedirectBaseUrl(localized, redirectBaseUrl) || '/';
    return (
      <Link
        href={href}
        onClick={handleLogoClick}
        {...getCSLPAttributes(props.logo_section?.$?.logo_link)}
      >
        <ImageWrapper image={desktop_logo} wrapperClassName="hidden md:block" />
        <ImageWrapper image={mobile_logo} wrapperClassName="block md:hidden" />
      </Link>
    );
  }
  return (
    <>
      <ImageWrapper image={desktop_logo} wrapperClassName="hidden md:block" />
      <ImageWrapper image={mobile_logo} wrapperClassName="block md:hidden" />
    </>
  );
};

const Default = (props: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const { base, topBar, topBarContainer, topBarActions, searchWrapper, mobileSearchWrapper } =
    desktopHeaderVariants({ menuVisible: false });

  const {
    globalLabels: { search_labels },
  } = useGlobalLabels();
  return (
    <>
      <header className={base()} data-id="Header">
        {props.emergencyAlert}

        <div className={topBar()}>
          <div className={topBarContainer()}>
            <Logo {...props} />
            <div className={topBarActions()}>
              <div className={searchWrapper()}>
                <SearchBar
                  placeholder={search_labels?.search_placeholder}
                  submitLabel={search_labels?.search_label}
                  wideDropdown
                  redirectSearchBaseUrl={props.redirectSearchBaseUrl}
                />
              </div>
              <LanguageSelector
                entryUid={props.pageUid}
                contentTypeUid={props.pageContentTypeUid}
                hasAlternateLanguage={props.hasAlternateLanguage}
                englishUrl={props.englishUrl}
                spanishUrl={props.spanishUrl}
              />
            </div>
            <MobileNav
              mobileMenuOpen={mobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
              mobileSearchOpen={mobileSearchOpen}
              setMobileSearchOpen={setMobileSearchOpen}
              entryUid={props.pageUid}
              contentTypeUid={props.pageContentTypeUid}
              hasAlternateLanguage={props.hasAlternateLanguage}
              englishUrl={props.englishUrl}
              spanishUrl={props.spanishUrl}
            />
          </div>
        </div>
        {/* Mobile search bar — shown below top bar when search icon is toggled */}
        <div className={mobileSearchWrapper({ searchVisible: mobileSearchOpen })}>
          <SearchBar
            showSubmitButton
            placeholder={search_labels?.search_placeholder}
            submitLabel={search_labels?.search_label}
            redirectSearchBaseUrl={props.redirectSearchBaseUrl}
          />
        </div>
        <DesktopHeader {...props} />
        <MobileHeader
          {...props}
          mobileMenuOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />
      </header>
    </>
  );
};

const variants = {
  Default,
};

export const Header = (props: HeaderProps) => {
  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};
